<template>
  <v-container v-if="$auth.check()">
    <v-row>
      <v-col>
        <h1>{{ $t('texts.addCompany') }}</h1>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-card-text>
            <v-container grid-list-md>
              <v-row>
                <v-col :cols="12" class="mb-2">
                  <google-place-autocomplete
                    ref="googleautocomplete"
                    v-model="form.name"
                    :model="form"
                    modelKey="name"
                    :label="$t('texts.nameOfCompany')"
                    prependIcon="search"
                    :types="['establishment']"
                    country="ch"
                    required
                    :data-vv-as="$t('texts.nameOfCompany')"
                    :error-messages="errors.collect('name')"
                    :autocompleteResponse="autocompleteResponse"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col :cols="12" :sm="8">
                  <v-text-field
                    :label="$t('texts.street')"
                    v-model="form.street"
                    required
                    data-vv-name="street"
                    :data-vv-as="$t('texts.street')"
                    v-validate="'required'"
                    :error-messages="errors.collect('street')"
                  />
                </v-col>
                <v-col :cols="12" :sm="4">
                  <v-text-field
                    :label="$t('texts.streetNumber')"
                    v-model="form.street_number"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col :cols="12" :sm="4">
                  <v-text-field
                    :label="$t('texts.zip')"
                    v-model="form.zip"
                    required
                    data-vv-name="zip"
                    :data-vv-as="$t('texts.zip')"
                    v-validate="'required|min:4|max:4'"
                    :error-messages="errors.collect('zip')"
                  />
                </v-col>
                <v-col :cols="12" :sm="8">
                  <v-text-field
                    :label="$t('texts.place')"
                    v-model="form.place"
                    required
                    data-vv-name="place"
                    :data-vv-as="$t('texts.place')"
                    v-validate="'required'"
                    :error-messages="errors.collect('place')"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-autocomplete
                    :items="countries"
                    v-model="form.country"
                    :label="$t('texts.country')"
                    item-value="iso"
                    item-text="name"
                    required
                    data-vv-name="country"
                    :data-vv-as="$t('texts.country')"
                    v-validate="'required'"
                    :error-messages="errors.collect('country')"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    :label="$t('texts.phone')"
                    v-model="form.phone"
                    required
                    data-vv-name="phone"
                    :data-vv-as="$t('texts.phone')"
                    v-validate="'required'"
                    :error-messages="errors.collect('phone')"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col>
                  <v-text-field
                    :label="$t('texts.website')"
                    v-model="form.website"
                    data-vv-name="website"
                    :data-vv-as="$t('texts.website')"
                    v-validate="'url'"
                    :error-messages="errors.collect('website')"
                  />
                </v-col>
              </v-row>
              <v-row>
                <v-col :cols="12">
                  <v-select
                    :items="companySizes"
                    v-model="form.size"
                    :label="$t('texts.numberOfEmployees')"
                    :data-vv-as="$t('texts.numberOfEmployees')"
                    data-vv-name="size"
                    v-validate="'required'"
                    :error-messages="errors.collect('size')"
                    required
                  >
                  <template
                    slot="item"
                    slot-scope="{item}"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ $t('texts.company.sizes.' + item) }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                  <template
                    slot="selection"
                    slot-scope="{item}"
                  >
                    <v-list-item-content>
                      <v-list-item-title>{{ $t('texts.company.sizes.' + item) }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                  </v-select>
                </v-col>
              </v-row>
            </v-container>
          </v-card-text>
          <v-divider/>
          <v-card-actions>
            <v-spacer/>
            <v-btn
              color="primary"
              class="white--text"
              @click="registerCompany()"
              :disabled="storeCompanyPending"
              :loading="storeCompanyPending"
            >
              <v-icon class="white--text" left>add_circle_outline</v-icon>
              {{ $t('texts.save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import GooglePlaceAutocomplete from '@/components/_organisms/GooglePlaceAutocomplete.vue';

const { mapState: mapGeneralState } = createNamespacedHelpers('general');
const { mapState: mapCompanyState } = createNamespacedHelpers('company');
const { mapState: mapCompanyProfileState, mapActions: mapCompanyProfileActions } = createNamespacedHelpers('company/profile');

export default {
  name: 'AddCompanyForm',
  components: {
    GooglePlaceAutocomplete,
  },
  data() {
    return {
      form: {},
    };
  },
  $_veeValidate: {
    validator: 'new',
  },
  created() {
    this.reset();
  },
  computed: {
    ...mapGeneralState([
      'countries',
    ]),
    ...mapCompanyState([
      'companySizes',
    ]),
    ...mapCompanyProfileState([
      'storeCompanyPending',
    ]),
  },
  methods: {
    ...mapCompanyProfileActions([
      'storeCompany',
    ]),
    reset() {
      this.form = {
        google_place_id: '',
        search: '',
        D: '',
        street: '',
        street_number: '',
        zip: null,
        place: '',
        country: 'CH',
        state: '',
        phone: '',
        website: '',
        latitude: '',
        longitude: '',
        size: '',
      };
      this.$validator.reset();
    },
    autocompleteResponse(address) {
      this.form.name = address.name || '';
      this.form.google_place_id = address.place_id || '';
      this.form.street = address.route || '';
      this.form.street_number = address.street_number || '';
      this.form.zip = address.postal_code || '';
      this.form.place = address.locality || '';
      this.form.state = address.administrative_area_level_1 || '';
      this.form.latitude = address.latitude || '';
      this.form.longitude = address.longitude || '';
      this.form.phone = address.phone || '';
      this.form.website = address.website || '';
      this.form.country = address.country || '';
      this.$refs.googleautocomplete.update(this.form.name);
    },
    registerCompany() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.storeCompany(this.form).then((response) => {
            this.$router.push({ name: 'company.profile.general', params: { companyId: response.id } });
            this.reset();
          });
        }
      });
    },
  },
};
</script>
